import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();

  const certifications = ['IATF 16949:2016', 'ISO 14001:2015', 'ISO 45001:2018'];

  return (
    <footer className="surface-dark grain relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)',
          backgroundSize: '96px 96px',
        }}
        aria-hidden
      />
      <div className="container-tight relative z-10 pt-20 pb-10">
        {/* Upper — giant wordmark */}
        <div className="mb-16">
          <div className="flex items-baseline justify-between mb-6">
            <span className="eyebrow text-secondary">— Artifex Systems Slovakia</span>
            <span className="font-display text-xs tracking-[0.2em] text-white/50 uppercase">Lozorno · Slovakia</span>
          </div>
          <div className="font-display font-bold text-white leading-[0.85] tracking-[-0.04em]" style={{ fontSize: 'clamp(3.5rem, 13vw, 12rem)' }}>
            ARTIFEX<span className="text-secondary">.</span>
          </div>
        </div>

        <div className="hairline" style={{ background: 'hsl(0 0% 100% / 0.14)' }} />

        {/* Mid — grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-14">
          <div className="col-span-2">
            <h4 className="font-display text-xs uppercase tracking-[0.2em] text-white/50 mb-5">— {t('sub.company')}</h4>
            <p className="text-white/75 leading-relaxed max-w-md mb-6">
              {t('footer.description')}
            </p>
            <div className="flex flex-wrap gap-2">
              {certifications.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center px-3 py-1.5 rounded-full border border-white/20 text-xs font-display font-medium text-white/85 tracking-wider"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display text-xs uppercase tracking-[0.2em] text-white/50 mb-5">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {[
                { label: t('nav.home'), href: '/#home' },
                { label: t('nav.about'), href: '/#about' },
                { label: t('nav.leadership'), href: '/#leadership' },
                { label: t('nav.quality'), href: '/#quality' },
                { label: t('nav.whistleblower'), href: '/whistleBlower' },
                { label: t('nav.careers'), href: '/#careers' },
                { label: t('nav.contact'), href: '/#contact' },
              ].map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="group inline-flex items-center gap-1.5 font-display text-white/80 hover:text-secondary transition-colors">
                    {l.label}
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-xs uppercase tracking-[0.2em] text-white/50 mb-5">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3 text-white/80 font-display">
              <li>Automobilový priemyselný park</li>
              <li>Lozorno 1006</li>
              <li>900 31 Lozorno, SK</li>
              <li className="pt-2">
                <a href="mailto:office-loz@artifex-systems.com" className="hover:text-secondary transition-colors">
                  office-loz@artifex-systems.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="hairline" style={{ background: 'hsl(0 0% 100% / 0.14)' }} />

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-white/50 text-xs font-display tracking-wider">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-6">
            <a href="/admin" className="text-white/60 hover:text-white text-xs font-display tracking-wider transition-colors">
              {t('footer.admin')}
            </a>
            <Link to="/whistleBlower" className="text-white/60 hover:text-white text-xs font-display tracking-wider transition-colors">
              {t('footer.whistleblower')}
            </Link>
            <a href="#" className="text-white/60 hover:text-white text-xs font-display tracking-wider transition-colors">
              {t('footer.privacy')}
            </a>
            <a href="#" className="text-white/60 hover:text-white text-xs font-display tracking-wider transition-colors">
              {t('footer.terms')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
